import React, { useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'

// evertel
import { useService } from '@evertel/di'
import { useDidMountEffect } from '@evertel/hooks'
import { Button, Icon, Row, Text, Card, CardBody, useUI, Media, Tooltip, TooltipTrigger, TooltipContent } from '@evertel/web/ui'
import { NormalizedMedia, UploadableMedia } from '@evertel/media'
import { useDocumentService } from '@evertel/service'
import { APIDataMedia } from '@evertel/types'
import Wrapper from './Wrapper'
import { DocumentMediaWriteWidgetController } from '../../controller/widgets/DocumentMediaWriteWidgetController'


export interface MediaWriteWidgetProps {
    id: string
    htmlId?: string
    value: number[]
    onChange: (id: string, value: {
        uploaded: NormalizedMedia[]
        deleteQueue: number[]
        uploadQueue: UploadableMedia[]
    }) => void
    title: string
    required?: boolean
    hint?: string
    placeholder?: string
    description?: string
    label?: string
    scope?: string
    errors?: string
    allowMultiple?: boolean
    visualMedia?: boolean
    documentId?: number
    typesAllowed?: ['images' | 'videos' | 'files' | 'audio' | 'application' | 'text'] | string
}

const MediaWriteWidget: React.FC<MediaWriteWidgetProps> = observer(({
    id,
    htmlId,
    value,
    onChange,
    title,
    placeholder,
    errors,
    required,
    scope,
    allowMultiple = true,
    typesAllowed,
    visualMedia,
    documentId,
    ...otherProps
}) => {

    const [isInitialized, setIsInitialized] = useState(false)
    const mediaWriteWidgetController = useService(DocumentMediaWriteWidgetController, [])
    const { addToast } = useUI()

    const inputId = `input-${htmlId}`
    const labelId = `label-${htmlId}`
    const descriptionId = `description-${htmlId}`
    const errorId = `error-${htmlId}`
    const hasErrors = errors && errors.length > 0

    // additional needed for file types allowed
    const inputDescriptionId = `input-description-${htmlId}`

    const meta = useDocumentService<{ media: APIDataMedia[] }>('meta')

    const existingMedia = useMemo(() => {
        if (!meta?.media || !value) return []

        // old bad-data case
        // @ts-expect-error historical issue
        const uploaded = value?.uploaded
        const raw = uploaded ?? value

        const ids = Array.isArray(raw) ? raw : [raw]

        return meta.media.filter((m) => ids.some((v) => v === m.id))
        //don't ever want to retrigger this since 'value' is different after first load
    }, []) //[meta?.media, value])

    useEffect(() => {
        mediaWriteWidgetController.init(existingMedia)
        setIsInitialized(true)
    }, [])

    useDidMountEffect(() => {
        if (onChange && scope !== 'schemaBuilder' && isInitialized) {
            onChange(id, mediaWriteWidgetController.mediaArrays)
        }
    }, [mediaWriteWidgetController.mediaArrays])

    const fileTypes = useMemo(() => {
        if (!typesAllowed) return undefined

        if (Array.isArray(typesAllowed)) {
            const types: string[] = []

            if (typesAllowed.includes('images')) types.push('image/*')
            if (typesAllowed.includes('videos')) types.push('video/*')
            if (typesAllowed.includes('audio'))  types.push('audio/*')
            if ( typesAllowed.includes('files') || typesAllowed.includes('application') ) {
                types.push('application/*', 'text/*')
            }

            return types.join(',')
        }

        return typesAllowed
    }, [typesAllowed])

    const getFileFormats = () => {
        const typesSet = new Set()

        if (typesAllowed?.includes('images') || !typesAllowed) {
            typesSet.add('jpg, jpeg')
            typesSet.add('png')
        }
        if (typesAllowed?.includes('videos') || !typesAllowed) {
            typesSet.add('mp4')
            typesSet.add('mov')
            typesSet.add('wmv')
        }
        if (typesAllowed?.includes('audio') || !typesAllowed) {
            typesSet.add('mp3')
            typesSet.add('m4a')
            typesSet.add('wav')
            typesSet.add('aac')
        }
        if (typesAllowed?.includes('application') || typesAllowed?.includes('files') || !typesAllowed) {
            typesSet.add('pdf')
            typesSet.add('docx')
            typesSet.add('xlsx')
            typesSet.add('txt')
            typesSet.add('ppt')
        }
        return Array.from(typesSet).join(', ')
    }

    const onUpload = async (files: File[]) => {
        try {
            const allowed = Array.isArray(typesAllowed) ? typesAllowed : typesAllowed ? [typesAllowed] : undefined
            await mediaWriteWidgetController.addToUploadQueue(files, allowed)
        } catch (error: any) {
            addToast({
                color: 'danger',
                title: 'Something went wrong',
                message: error.message
            })
        }
    }

    const sharedMediaProps = {
        width: 50,
        height: 50,
        className: 'me-3'
    }

    const { uploaded, uploadQueue } = mediaWriteWidgetController.mediaArrays
    const total = (uploaded?.length ?? 0) + (uploadQueue?.length ?? 0)
    const moreAllowed = allowMultiple || total === 0

    return (
        <Wrapper
            title={title}
            required={required}
            errors={errors}
            labelId={labelId}
            descriptionId={descriptionId}
            errorId={errorId}
            {...otherProps}
        >
            <Card>
                <CardBody>
                    <Tooltip>
                        <TooltipTrigger className="fw-bold" asChild={true}>
                            <Button
                                id={inputId}
                                color="primary"
                                disabled={scope === 'schemaBuilder'}
                                className={classNames({disabled: !moreAllowed})}
                                outline
                                upload={moreAllowed ? ((allowMultiple) ? 'multiple' : true) : false}
                                onUpload={onUpload}
                                fileTypes={fileTypes}
                                aria-labelledby={(title || otherProps.hint) ? labelId : undefined}
                                aria-invalid={hasErrors ? true : undefined}
                                aria-errormessage={hasErrors ? errorId : undefined}
                                aria-required={required ? true : undefined}
                                aria-describedby={inputDescriptionId}
                                style={{ pointerEvents: !moreAllowed ? 'auto' : undefined }}
                            >
                                <Icon
                                    name="upload"
                                    className="me-1"
                                />
                                { placeholder || 'Add File' }
                            </Button>
                        </TooltipTrigger>
                        { !moreAllowed && (
                            <TooltipContent>Only a single file is alowed, please remove selected to choose another.</TooltipContent>
                        )}
                    </Tooltip>
                    <div>
                        <small
                            id={inputDescriptionId}
                            color="body-color"
                            className="mt-2"
                        >
                            (Allowed file types: {typesAllowed?.length ? getFileFormats() : 'Any'})
                            {!allowMultiple && (
                                <>
                                    <br />
                                    (Single File)
                                </>
                            )}
                        </small>
                    </div>
                    <Row className="mt-4 flex-wrap" role="group">
                        <span className="visually-hidden">List of previously updated media:</span>
                        {uploaded?.map((m) =>
                            <Media
                                key={`document-media-${m.id}`}
                                media={m}
                                variant="file-attachment"
                                fileProps={{width: 200}}
                                videoProps={{ asThumbnail: true }}
                                imageProps={{ flexibility: 'fixed' }}
                                resizeMode="cover"
                                rounded
                                onDelete={() => mediaWriteWidgetController.deleteUploaded(m.id)}
                                {...sharedMediaProps}
                            />
                        )}
                    </Row>
                    {uploadQueue.length > 0 && <p className="m-2" aria-hidden={true}>Added media:</p>}
                    <Row className="flex-wrap" role="group" aria-live="polite">
                        <span className="visually-hidden">Newly added media:</span>
                        {uploadQueue?.map((m) => (
                            <Media
                                key={`document-media-${m.id}`}
                                media={m}
                                variant="file-attachment"
                                fileProps={{width: 200}}
                                videoProps={{ asThumbnail: true }}
                                imageProps={{ flexibility: 'fixed' }}
                                resizeMode="cover"
                                rounded
                                onDelete={() => mediaWriteWidgetController.deleteFromUploadQueue(m.id)}
                                {...sharedMediaProps}
                            />
                        ))}
                    </Row>
                </CardBody>
            </Card>
        </Wrapper>
    )
})

export {
    MediaWriteWidget
}
